import '../theme/text.css';
import '../theme/breakpoints.css';
import '../theme/mixins.css';
import './Calendar.css';

import { FC, Fragment, useMemo, useState } from 'react';
import { AppLink, ExternalLink } from 'components/core/link/Link';
import { Select } from 'components/form/Select';
import { startOfDay } from 'date-fns';

import { Slot } from '../api/getAvailability';
import { Text } from '../theme/Text';
import { forRange } from '../utilities/forRange';
import { CalendarDayViewModel } from './calendar-utils';
import { useCalendarContext } from './CalendarProvider';
import { SlotHolster } from './SlotHolster';
import { TimeZoneOptions } from './timezones';

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

const Day: FC<{
  day: CalendarDayViewModel;
  disabled?: boolean;
  onClick: () => void;
  active?: boolean;
  isFirstWeek?: boolean;
}> = ({ day, disabled, onClick, active }) => {
  const empty = !day;

  return (
    <button
      className={`day ${empty ? 'empty' : ''} ${disabled ? 'disabled' : ''} ${active ? 'active' : ''}`}
      onClick={onClick}
    >
      {day?.showMonth && <span className="month-marker">{MONTHS[day.date.getMonth()]}</span>}
      {day ? day.date.getDate() : null}
    </button>
  );
};

export interface CalendarProps {
  onSelectSlot: (slot: Slot) => void;
  showSkipButton?: boolean;
}

export const Calendar: FC<CalendarProps> = ({ onSelectSlot, showSkipButton = false }) => {
  const {
    availableDays,
    slots,
    setSelectedDay,
    selectedDay,
    dayViewModels,
    placeholderViewModels,
    defaultTimeZone,
    changeTimeZone,
  } = useCalendarContext();
  const today = useMemo(() => startOfDay(new Date()), []);

  const defaultOption = useMemo(() => {
    return TimeZoneOptions.find((o) => o.value === defaultTimeZone);
  }, [defaultTimeZone]);

  const [selectedWeekIndex, setSelectedWeekIndex] = useState<number | null>(null);

  const setDay = (selectedDay: string | null, selectedWeekIndex: number) => {
    setSelectedDay(selectedDay).then(() => {
      setSelectedWeekIndex(selectedDay === null ? null : selectedWeekIndex);
    });
  };

  return (
    <div className="Calendar">
      <Select
        label="Timezone"
        options={TimeZoneOptions}
        defaultValue={defaultOption}
        onChange={(v) => v && changeTimeZone(v.value)}
      />
      <div className="week">
        <div className="day header">Sun</div>
        <div className="day header">Mon</div>
        <div className="day header">Tue</div>
        <div className="day header">Wed</div>
        <div className="day header">Thu</div>
        <div className="day header">Fri</div>
        <div className="day header">Sat</div>
      </div>
      {placeholderViewModels.map((_, i) =>
        dayViewModels[i] ? (
          <Fragment key={i}>
            <div className="week">
              {dayViewModels[i].map((day, j) => (
                <Day
                  day={day}
                  disabled={day ? day.date < today || !availableDays?.has(day.formatted) : true}
                  key={day?.formatted ?? j}
                  onClick={() => setDay(day?.formatted ?? null, i)}
                  active={!!selectedDay && selectedDay === day?.formatted}
                  isFirstWeek={i === 0}
                />
              ))}
              {dayViewModels[i].length < 7 &&
                forRange(0, 7 - dayViewModels[i].length, (i) => (
                  <Day day={null} disabled key={i} onClick={() => null} />
                ))}
            </div>
            {i === selectedWeekIndex && selectedDay && (
              <SlotHolster day={selectedDay} slots={slots ?? []} onSelectSlot={onSelectSlot} />
            )}
          </Fragment>
        ) : (
          <div className="week" key={i} data-testid="availability-loading">
            <div
              className="skeleton-wrapper"
              style={{
                display: 'flex',
                height: 40,
                width: '100%',
                margin: '3px',
              }}
            >
              <div className="skeleton"></div>
            </div>
          </div>
        ),
      )}
      <div className="calendar-footer">
        {showSkipButton && (
          <AppLink style={{ color: 'var(--neutral-gray-5)' }} to="/home">
            Skip for now
          </AppLink>
        )}
        <div>
          <Text variant="body-7">
            If you’re having trouble finding a time that works for you, call us at{' '}
            <ExternalLink href="tel:+18005160975" style={{ fontSize: 12, fontWeight: 'bold' }}>
              1-800-516-0975
            </ExternalLink>{' '}
            for support.
          </Text>
        </div>
      </div>
    </div>
  );
};
